import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import {
  Home as HomeIcon,
  MessageCircle,
  Users,
  HelpingHand,
  Stethoscope,
  GraduationCap,
  Calendar,
  LifeBuoy,
  User,
  Heart,
  LogOut,
  Menu,
  X,
  Landmark,
  Heart as HeartIcon,
  Sparkles,
} from "lucide-react";
import { useAuth } from "../../lib/auth";
import { useCapabilities } from "../../lib/useCapabilities";
import { PORTAL } from "../../constants/testIds";
import BtcLogo from "../../components/BtcLogo";
import NorthstarBreadcrumb from "../../components/northstar/NorthstarBreadcrumb";
import useIsDesktop from "../../hooks/useIsDesktop";

const nav = [
  { to: "/portal/dashboard", icon: HomeIcon, label: "Dashboard", tid: PORTAL.navDashboard },
  { to: "/portal/matches", icon: Sparkles, label: "Your Buddy matches", tid: "portal-nav-matches" },
  { to: "/portal/sessions", icon: Calendar, label: "Sessions", tid: "portal-nav-sessions" },
  { to: "/portal/messaging", icon: MessageCircle, label: "Messaging", tid: PORTAL.navMessaging },
  { to: "/portal/community", icon: Users, label: "Community", tid: PORTAL.navCommunity },
  { to: "/portal/volunteer", icon: HelpingHand, label: "Volunteer Hub", tid: PORTAL.navVolunteer },
  { to: "/portal/hospitals", icon: Stethoscope, label: "Hospitals", tid: PORTAL.navHospital },
  { to: "/portal/resources", icon: GraduationCap, label: "Resources", tid: PORTAL.navResources },
  { to: "/portal/events", icon: Calendar, label: "Events", tid: PORTAL.navEvents },
  { to: "/portal/support-requests", icon: LifeBuoy, label: "Support Requests", tid: PORTAL.navSupport },
  { to: "/portal/profile", icon: User, label: "Profile", tid: PORTAL.navProfile },
];

const adminNav = [
  { to: "/portal/admin/donations", icon: Landmark, label: "Donations", tid: "portal-nav-admin-donations" },
  { to: "/portal/admin/buddies", icon: HeartIcon, label: "Buddy programme", tid: "portal-nav-admin-buddies" },
];

export default function PortalLayout() {
  const { user, loading, logout } = useAuth();
  const { caps } = useCapabilities();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  // BTC3 Phase 11 Stage A1 — reactive breakpoint source.  The drawer's
  // aria-hidden must flip when the viewport crosses the lg breakpoint
  // (1024 px) WITHOUT depending on an unrelated re-render.
  const isDesktop = useIsDesktop();
  // Collapse the open-drawer state once the viewport is wide enough
  // for the sticky sidebar — this avoids a stuck backdrop and scroll
  // lock on resize up from mobile.
  useEffect(() => {
    if (isDesktop && open) {
      setOpen(false);
    }
  }, [isDesktop, open]);

  // Phase 11 — drawer accessibility: body scroll locks while open,
  // ESC closes the drawer, and focus is managed on the trigger/close
  // buttons for keyboard and screen-reader users.
  useEffect(() => {
    if (!open) {
      document.body.classList.remove("btc-scroll-locked");
      return undefined;
    }
    document.body.classList.add("btc-scroll-locked");
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => closeButtonRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
      document.body.classList.remove("btc-scroll-locked");
    };
  }, [open]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FB]">
        <Heart className="w-8 h-8 text-[#23803F] animate-pulse" />
      </div>
    );
  }
  if (!user || typeof user !== "object") {
    return <Navigate to="/login" replace />;
  }

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div data-testid={PORTAL.layout} className="min-h-screen bg-[#F8F9FB] flex">
      {/* Sidebar */}
      <aside
        id="portal-mobile-drawer"
        data-testid={PORTAL.sidebar}
        role="navigation"
        aria-label="Portal navigation"
        aria-hidden={isDesktop ? undefined : !open}
        className={`fixed lg:sticky top-0 left-0 h-screen w-72 bg-white border-r border-gray-100 z-40 transform transition-transform btc-safe-y ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <Link to="/" className="flex items-center gap-2">
            <BtcLogo className="h-11 w-auto" />
          </Link>
          <button
            ref={closeButtonRef}
            className="lg:hidden text-[#0B132B] w-11 h-11 inline-flex items-center justify-center"
            aria-label="Close menu"
            data-testid="portal-drawer-close"
            onClick={() => setOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="px-3 py-5 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.tid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                  isActive
                    ? "bg-[#23803F]/10 text-[#23803F] font-semibold btc-portal-nav-active"
                    : "text-[#0B132B] hover:bg-[#F8F9FB]"
                }`
              }
            >
              <n.icon className="w-4 h-4" aria-hidden="true" />
              {n.label}
            </NavLink>
          ))}
          {caps.staff_administration && (
            <>
              <div className="px-3 pt-5 pb-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Admin
              </div>
              {adminNav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={n.tid}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                      isActive
                        ? "bg-[#1A5FAB]/10 text-[#1A5FAB] font-semibold btc-portal-nav-active"
                        : "text-[#0B132B] hover:bg-[#F8F9FB]"
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" aria-hidden="true" />
                  {n.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 px-3 py-4 border-t border-gray-100 bg-white">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-9 h-9 rounded-full bg-[#1A5FAB] text-white flex items-center justify-center text-xs font-bold">
              {(user.name || user.email)?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-[#0B132B] truncate">{user.name || user.email}</div>
              <div className="text-[11px] text-[#4B5563] capitalize">{user.role}</div>
            </div>
          </div>
          <button
            data-testid={PORTAL.logoutButton}
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#4B5563] hover:text-red-600 transition"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => {
            setOpen(false);
            menuButtonRef.current?.focus();
          }}
          aria-hidden="true"
          data-testid="portal-drawer-backdrop"
        />
      )}

      <div className="flex-1 lg:ml-0 min-w-0">
        <header
          className="h-16 bg-white border-b border-gray-100 sticky top-0 z-20 flex items-center px-4 lg:px-8 gap-4 btc-safe-x"
          aria-label="Portal toolbar"
        >
          <button
            ref={menuButtonRef}
            className="lg:hidden text-[#0B132B] w-11 h-11 inline-flex items-center justify-center"
            aria-label="Open menu"
            aria-expanded={open}
            aria-controls="portal-mobile-drawer"
            data-testid="portal-drawer-toggle"
            onClick={() => setOpen(true)}
          >
            <Menu className="w-6 h-6" aria-hidden="true" />
          </button>
          {/* Phase 10 F-R-01 / F-R-02 · Truthful UI retirement.
              The portal header previously carried a placeholder search
              input and a notification bell button, neither of which
              had a persisted backend capability.  Both are retired for
              Phase 10 to match the Phase 6 posture already applied to
              the public Header.  No search or notifications feature is
              invented. */}
          <div className="flex-1" aria-hidden="true" />
        </header>
        <NorthstarBreadcrumb />
        <main id="main-content" className="p-4 lg:p-8 max-w-[1600px]">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
